import type { AccountKind } from '@hvac/contracts';
import { Alert, Button, Field, Input, Skeleton } from '@hvac/ui';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { requestPasswordReset, signUp, startAccount } from '../../shared/api/billing-queries.js';
import { recordSignIn } from '../../shared/api/queries.js';
import { useAuth } from '../../shared/auth/auth-context.js';
import { supabase } from '../../shared/auth/supabase.js';

/**
 * Arriving without an invitation.
 *
 * A firm or a business opens its own account and starts a thirty-day trial
 * with no card. Where email is switched on, the address is confirmed by a
 * link first; where it is not, the account is ready at once. Either way the
 * next stop is setting up the first business.
 */

function Frame({
  title,
  intro,
  children,
}: {
  title: string;
  intro: ReactNode;
  children: ReactNode;
}) {
  return (
    <main className="flex min-h-dvh items-center justify-center px-4 py-12 sm:px-6">
      <div className="w-full max-w-md">
        <p className="mb-8 flex items-center gap-2.5">
          <span className="flex size-8 items-center justify-center rounded-[9px] bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]">
            P
          </span>
          <span className="text-sm font-semibold tracking-tight text-content">
            Pathara <span className="text-brand-600">Finance</span>
          </span>
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-content">{title}</h1>
        <div className="mt-1 text-sm text-content-muted">{intro}</div>
        <div className="mt-8">{children}</div>
      </div>
    </main>
  );
}

const KIND_CHOICES: readonly { kind: AccountKind; title: string; detail: string }[] = [
  {
    kind: 'firm',
    title: 'An accounting firm',
    detail: 'Keep the books for many businesses, each one isolated, from one desk.',
  },
  {
    kind: 'business',
    title: 'A business',
    detail: 'Your own books, ready for your accountant.',
  },
];

export function SignUpPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [kind, setKind] = useState<AccountKind>('firm');
  const [accountName, setAccountName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      const result = await signUp({
        kind,
        accountName: accountName.trim(),
        fullName: fullName.trim(),
        email: email.trim(),
        password,
      });
      if (result.needsConfirmation) return 'confirm' as const;
      await auth.signIn(email.trim(), password);
      void recordSignIn();
      await startAccount({ kind, accountName: accountName.trim() });
      return 'ready' as const;
    },
    onSuccess: (outcome) => {
      if (outcome === 'confirm') setSentTo(email.trim());
      else void navigate({ to: '/organizations/new' });
    },
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The account could not be opened.'),
  });

  if (sentTo !== null) {
    return (
      <Frame title="Check your email" intro={<>A link is on its way to {sentTo}.</>}>
        <p className="text-sm text-content-muted">
          Open it on this device to confirm the address. It brings you straight back here to set up
          the first business. If nothing arrives in a few minutes, look in junk mail.
        </p>
      </Frame>
    );
  }

  const ready =
    accountName.trim().length >= 2 &&
    fullName.trim().length >= 2 &&
    email.includes('@') &&
    password.length >= 10;

  return (
    <Frame
      title="Start a free trial"
      intro="Thirty days, every feature, no card. Client books are isolated from the first row."
    >
      {error === null ? null : (
        <div className="mb-4">
          <Alert tone="danger">{error}</Alert>
        </div>
      )}
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          submit.mutate();
        }}
      >
        <fieldset className="grid gap-2 sm:grid-cols-2">
          <legend className="mb-2 text-sm font-medium text-content">This account is for</legend>
          {KIND_CHOICES.map((choice) => (
            <label
              key={choice.kind}
              className={`cursor-pointer rounded-[var(--radius-control)] border p-3 text-sm ${
                kind === choice.kind
                  ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                  : 'border-border-strong bg-surface'
              }`}
            >
              <input
                type="radio"
                name="kind"
                value={choice.kind}
                checked={kind === choice.kind}
                onChange={() => setKind(choice.kind)}
                className="sr-only"
              />
              <span className="block font-medium text-content">{choice.title}</span>
              <span className="mt-0.5 block text-xs text-content-muted">{choice.detail}</span>
            </label>
          ))}
        </fieldset>
        <Field label={kind === 'firm' ? 'Firm name' : 'Business name'}>
          {({ inputProps }) => (
            <Input
              {...inputProps}
              value={accountName}
              autoComplete="organization"
              onChange={(event) => setAccountName(event.target.value)}
            />
          )}
        </Field>
        <Field label="Your name">
          {({ inputProps }) => (
            <Input
              {...inputProps}
              value={fullName}
              autoComplete="name"
              onChange={(event) => setFullName(event.target.value)}
            />
          )}
        </Field>
        <Field label="Work email">
          {({ inputProps }) => (
            <Input
              {...inputProps}
              type="email"
              value={email}
              autoComplete="username"
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label="Choose a password" hint="At least ten characters.">
          {({ inputProps }) => (
            <Input
              {...inputProps}
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>
        <Button type="submit" size="lg" isLoading={submit.isPending} disabled={!ready}>
          Open the account
        </Button>
        <p className="text-xs leading-relaxed text-content-subtle">
          Already have an account?{' '}
          <Link to="/login" search={{}} className="text-brand-600 hover:underline">
            Sign in
          </Link>
          .
        </p>
      </form>
    </Frame>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => requestPasswordReset(email.trim()),
    onSuccess: () => setSent(true),
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The link could not be sent.'),
  });

  if (sent) {
    return (
      <Frame
        title="Check your email"
        intro={<>If {email.trim()} has an account, a link is on its way.</>}
      >
        <p className="text-sm text-content-muted">
          It works once and expires within the hour.{' '}
          <Link to="/login" search={{}} className="text-brand-600 hover:underline">
            Back to sign in
          </Link>
        </p>
      </Frame>
    );
  }

  return (
    <Frame
      title="Choose a new password"
      intro="We will email a link to the address on the account."
    >
      {error === null ? null : (
        <div className="mb-4">
          <Alert tone="warning">{error}</Alert>
        </div>
      )}
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          send.mutate();
        }}
      >
        <Field label="Email">
          {({ inputProps }) => (
            <Input
              {...inputProps}
              type="email"
              value={email}
              autoComplete="username"
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Button type="submit" isLoading={send.isPending} disabled={!email.includes('@')}>
          Email me a link
        </Button>
        <Link to="/login" search={{}} className="text-sm text-content-muted hover:text-content">
          ← Back to sign in
        </Link>
      </form>
    </Frame>
  );
}

/** Where the emailed reset link lands, already signed in by the link. */
export function ResetPasswordPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      const { error: failed } = await supabase.auth.updateUser({ password });
      if (failed !== null) throw new Error(failed.message);
    },
    onSuccess: () => void navigate({ to: '/' }),
    onError: (caught) =>
      setError(caught instanceof Error ? caught.message : 'The password was not changed.'),
  });

  if (auth.status === 'loading')
    return (
      <Frame title="One moment" intro="">
        <Skeleton className="h-24" />
      </Frame>
    );
  if (auth.status === 'anonymous') {
    return (
      <Frame title="This link has expired" intro="Reset links work once and for an hour.">
        <Link to="/forgot-password" className="text-sm text-brand-600 hover:underline">
          Send a new link
        </Link>
      </Frame>
    );
  }

  return (
    <Frame title="Choose a new password" intro={<>For {auth.email}.</>}>
      {error === null ? null : (
        <div className="mb-4">
          <Alert tone="danger">{error}</Alert>
        </div>
      )}
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label="New password" hint="At least ten characters.">
          {({ inputProps }) => (
            <Input
              {...inputProps}
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>
        <Button type="submit" isLoading={save.isPending} disabled={password.length < 10}>
          Save and continue
        </Button>
      </form>
    </Frame>
  );
}

/**
 * Where the confirmation link lands. The link signs them in; what they said
 * at sign-up becomes their account, and setup starts.
 */
export function WelcomePage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (auth.status !== 'authenticated' || started.current) return;
    started.current = true;
    void recordSignIn();
    startAccount()
      .then(() => navigate({ to: '/organizations/new' }))
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : 'The account could not be started.'),
      );
  }, [auth.status, navigate]);

  if (auth.status === 'anonymous') {
    return (
      <Frame title="This link has expired" intro="Confirmation links work once.">
        <Link to="/login" search={{}} className="text-sm text-brand-600 hover:underline">
          Sign in instead
        </Link>
      </Frame>
    );
  }
  return (
    <Frame title="Address confirmed" intro="Opening your account…">
      {error === null ? <Skeleton className="h-24" /> : <Alert tone="danger">{error}</Alert>}
    </Frame>
  );
}
